// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { NumberTextField } from '@/components/number-text-field';
import { TextFieldProps } from '@mui/material';
import {
  Control,
  FieldValues,
  UseControllerProps,
  useController,
} from 'react-hook-form';
import { NumberFormatValues } from 'react-number-format';

interface Props<Values extends FieldValues>
  extends UseControllerProps<Values>,
    Pick<
      TextFieldProps,
      | 'label'
      | 'fullWidth'
      | 'placeholder'
      | 'disabled'
      | 'helperText'
      | 'InputProps'
      | 'InputLabelProps'
    > {
  /**
   * A react-hook-form control object used to connect the input element to the
   * form.
   */
  control: Control<Values>;

  /**
   * If true, the form value will be stored as a number. Otherwise, it will be
   * stored as a string.
   *
   * @default false
   */
  numeric?: boolean;

  isCurrency?: boolean;

  /**
   * If true, the input will display a $ prefix.
   *
   * @default false
   */
  showDollarPrefix?: boolean;

  /**
   * If true, the input will allow zero as a valid value.
   *
   * @default true
   */
  allowZero?: boolean;

  allowNegative?: boolean;

  /**
   * If true, the input can be nullable.
   *
   * @default true
   */
  nullable?: boolean;

  /**
   * The maximum number of fractional digits that can be entered.
   */
  maximumFractionDigits?: number;

  /**
   * Optional callback fired when the value changes.
   */
  onChange?: (values: NumberFormatValues) => void;
}

/**
 * A controlled number text field component that integrates with
 * react-hook-form.
 */
export function FormNumberTextField<Values extends FieldValues>(
  props: Props<Values>
) {
  const { field, fieldState } = useController({
    name: props.name,
    control: props.control,
  });

  // Use "true" as the default value for nullable, if not specified.
  const isNullable = props.nullable === undefined ? true : props.nullable;

  const value = field.value ?? (isNullable ? null : '');
  const handleChange = (values: NumberFormatValues) => {
    if (values.value === '') {
      // If the field is not nullable, we should not change the field value to null, even if blank.
      if (isNullable) {
        field.onChange(null);
      }
    } else if (props.numeric) {
      field.onChange(values.floatValue);
    } else {
      field.onChange(values.value);
    }

    // Call the optional onChange callback if provided
    if (props.onChange) {
      props.onChange(values);
    }
  };

  return (
    <NumberTextField
      size="small"
      name={field.name}
      value={value}
      label={props.label}
      error={Boolean(fieldState.error)}
      onBlur={field.onBlur}
      variant="outlined"
      inputRef={field.ref}
      onChange={handleChange}
      fullWidth={props.fullWidth}
      allowZero={props.allowZero}
      helperText={fieldState.error?.message ?? props.helperText}
      isCurrency={props.isCurrency}
      showDollarPrefix={props.showDollarPrefix}
      placeholder={props.placeholder}
      allowNegative={props.allowNegative}
      maximumFractionDigits={props.maximumFractionDigits}
      disabled={props.disabled}
      InputProps={props.InputProps}
      InputLabelProps={props.InputLabelProps}
    />
  );
}
